import React, { useCallback, useEffect, useState } from "react";
import { MessageSquare, Link2, Check, X, Sparkles, ShoppingBag } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Button, Chip, Skeleton, Toast } from "@/ui";

function LinkPicker({ post, products, onSave, onCancel, saving }) {
  const [productId, setProductId] = useState(post.productId || "");
  const [variantName, setVariantName] = useState(post.variantName || "");
  const product = products.find((p) => p.id === productId);
  const variants = product?.variants || [];
  return (
    <div className="stack" style={{ gap: 10, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
      <div className="text-sm font-medium">Which item does this post sell?</div>
      <select data-testid={`post-product-select-${post.postId}`} value={productId}
        onChange={(e) => { setProductId(e.target.value); setVariantName(""); }}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
        <option value="">Pick an item…</option>
        {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {variants.length > 0 ? (
        <select data-testid={`post-variant-select-${post.postId}`} value={variantName}
          onChange={(e) => setVariantName(e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">Any option</option>
          {variants.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
        </select>
      ) : null}
      <div className="flex gap-2">
        <Button data-testid={`post-save-link-${post.postId}`} disabled={!productId || saving}
          onClick={() => onSave(productId, variantName)}>
          {saving ? "Saving\u2026" : "Save"}
        </Button>
        <Button variant="ghost" data-testid={`post-cancel-link-${post.postId}`} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function Posts() {
  const [posts, setPosts] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [editing, setEditing] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pr, pd] = await Promise.all([api.get("/posts"), api.get("/products")]);
      setPosts(pr.data.posts || []);
      setProducts(pd.data.products || []);
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveLink = async (postId, productId, variantName) => {
    setSaving(true); setError("");
    try {
      const r = await api.post(`/posts/${postId}/link`, { productId, variantName: variantName || null });
      setToast(r.data.message || "Post linked");
      setEditing("");
      await load();
    } catch (e) { setError(friendlyError(e)); }
    finally { setSaving(false); }
  };
  const unlink = async (postId) => {
    setError("");
    try { await api.post(`/posts/${postId}/unlink`, {}); setToast("Unlinked"); await load(); }
    catch (e) { setError(friendlyError(e)); }
  };

  return (
    <Layout title="Posts" subtitle="Tap a post, pick what it sells. Comments turn into chats that already know the item.">
      {loading ? (
        <div className="grid grid-2" style={{ gap: 16 }}>{[1, 2, 3, 4].map((i) => <Card key={i}><Skeleton className="h-48" /></Card>)}</div>
      ) : posts.length === 0 ? (
        <Card data-testid="posts-empty" className="text-center py-10">
          <MessageSquare className="mx-auto mb-2 text-slate-400" size={28} />
          <div className="font-medium">No posts yet</div>
          <div className="text-sm text-slate-500">Connect your Facebook Page to see your recent posts here.</div>
        </Card>
      ) : (
        <div className="grid grid-2" style={{ gap: 16 }} data-testid="posts-grid">
          {posts.map((post) => (
            <Card key={post.postId} data-testid={`post-card-${post.postId}`} className="stack" style={{ gap: 10 }}>
              {post.picture ? (
                <img src={post.picture} alt="" data-testid={`post-image-${post.postId}`}
                  style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 12 }}
                  onError={(e) => { e.target.style.display = "none"; }} />
              ) : null}
              <div className="text-sm">{post.text}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <Chip tone="neutral"><MessageSquare size={12} /> {post.commentCount} comments</Chip>
                {post.linked ? (
                  <Chip tone="good" data-testid={`post-linked-${post.postId}`}>
                    <ShoppingBag size={12} /> Selling: {post.productName}{post.variantName ? ` (${post.variantName})` : ""}
                  </Chip>
                ) : (
                  <Chip tone="warn"><Sparkles size={12} /> Not linked yet</Chip>
                )}
              </div>
              {editing === post.postId ? (
                <LinkPicker post={post} products={products} saving={saving}
                  onSave={(pid, vn) => saveLink(post.postId, pid, vn)} onCancel={() => setEditing("")} />
              ) : (
                <div className="flex gap-2">
                  <Button data-testid={`post-link-btn-${post.postId}`} variant={post.linked ? "ghost" : "secondary"}
                    onClick={() => setEditing(post.postId)}>
                    <Link2 size={14} /> {post.linked ? "Change item" : "Link a product"}
                  </Button>
                  {post.linked ? (
                    <Button variant="ghost" data-testid={`post-unlink-btn-${post.postId}`} onClick={() => unlink(post.postId)}>
                      <X size={14} /> Unlink
                    </Button>
                  ) : null}
                </div>
              )}
              {post.linked ? (
                <div className="text-xs text-emerald-700 flex items-center gap-1">
                  <Check size={12} /> Comment-to-message is on for this post
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
      {error ? <div className="text-sm text-rose-600 mt-3" data-testid="posts-error">{error}</div> : null}
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="posts-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
